import { useMemo, useEffect } from 'react';
import { DadosDepreciacao } from '@/features/laudo/types/laudo-wizard.types';
import { validarIdadeVidaUtil, validarDepreciacao } from '@/features/laudo/utils/validacoes-laudo';
import { calcularPorcentagemVidaUtil, calcularDepreciacao, getCoeficienteHeidecke } from './utils';
import type { DadosCalculados, Alerta } from './types';

export const useDadosCalculados = (dados: DadosDepreciacao[]): DadosCalculados[] => {
  return useMemo(() => {
    return dados.map((item) => {
      const coefHeidecke = getCoeficienteHeidecke(item.estadoConservacao);
      const porcentagemVU = calcularPorcentagemVidaUtil(item.idadeAparente, item.vidaUtil);
      const depreciacao = calcularDepreciacao(item.idadeAparente, item.vidaUtil, coefHeidecke, item.valorResidual ?? 0.2);

      return {
        ...item,
        coefHeidecke,
        porcentagemVU,
        depreciacao,
      };
    });
  }, [dados]);
};

export const useSincronizarDados = (
  dadosCalculados: DadosCalculados[],
  dados: DadosDepreciacao[],
  onUpdate: (dados: DadosCalculados[]) => void
) => {
  useEffect(() => {
    const precisaAtualizar = dadosCalculados.some((itemCalculado, index) => {
      const itemOriginal = dados[index];
      if (!itemOriginal) return false;

      return (
        Math.abs(itemCalculado.depreciacao - (itemOriginal.depreciacao || 0)) > 0.0001 ||
        Math.abs(itemCalculado.coefHeidecke - (itemOriginal.coefHeidecke || 0)) > 0.0001 ||
        Math.abs(itemCalculado.porcentagemVU - (itemOriginal.porcentagemVU || 0)) > 0.01
      );
    });

    if (precisaAtualizar) {
      onUpdate(dadosCalculados);
    }
  }, [dadosCalculados, dados, onUpdate]);
};

export const useDadosFiltrados = (dadosCalculados: DadosCalculados[]) => {
  return useMemo(() => {
    return dadosCalculados.filter((item) => item.tipoImovel !== 'Terreno');
  }, [dadosCalculados]);
};

export const useAlertas = (dadosCalculados: DadosCalculados[], displayIdMap?: Record<string, string>): Alerta[] => {
  return useMemo(() => {
    const alertasList: Alerta[] = [];

    dadosCalculados.forEach((item) => {
      const label = displayIdMap?.[item.identificacao] || item.identificacao;
      const validacaoIdade = validarIdadeVidaUtil(item.idadeAparente, item.vidaUtil);
      if (validacaoIdade) {
        alertasList.push({
          tipo: validacaoIdade.tipo as 'erro' | 'alerta',
          message: `${label}: ${validacaoIdade.mensagem} - ${validacaoIdade.detalhes}`,
        });
      }

      const validacaoDepreciacao = validarDepreciacao(item.depreciacao);
      if (validacaoDepreciacao) {
        alertasList.push({
          tipo: validacaoDepreciacao.tipo as 'erro' | 'alerta',
          message: `${label}: ${validacaoDepreciacao.mensagem} - ${validacaoDepreciacao.detalhes}`,
        });
      }
    });

    return alertasList;
  }, [dadosCalculados, displayIdMap]);
};

export const useAmostrasComProblemas = (dadosCalculados: DadosCalculados[]): Set<string> => {
  return useMemo(() => {
    const identificacoes = new Set<string>();

    dadosCalculados.forEach((item) => {
      const validacaoIdade = validarIdadeVidaUtil(item.idadeAparente, item.vidaUtil);
      const validacaoDepreciacao = validarDepreciacao(item.depreciacao);

      if (validacaoIdade || validacaoDepreciacao) {
        identificacoes.add(item.identificacao);
      }
    });

    return identificacoes;
  }, [dadosCalculados]);
};
